import React, { useState } from 'react';
import { get, isEmpty, forEach, split, includes, map } from 'lodash';
import { oneOfType, array, object, string } from 'prop-types';
import { format } from 'rut.js';
import * as yup from 'yup';
import { Text, Row, Column, Button, AppIcon, FlexContainer, Modal, FormInput, HasPermissions } from '../../common';
import { currentUserInfo, validateRut } from '../../../utils/misc';
import { Mutation } from '../../graphql';
import { UPDATE_ORDER } from '../../../queries';

const documentSchema = yup.string()
    .test('documentId', 'document-El RUT ingresado no es válido',
        function (value) {
            if (isEmpty(value)) return true;
            return validateRut(value);
    })
    .nullable();
    
const nameSchema = (field) => yup.string()
    .required(`${field}-Campo obligatorio`)
    .matches(/^[aA-zZ\s]+$/, `${field}-No se permiten números ni caracteres especiales`)
    .nullable();

const validationSchema = yup.object().shape({
    firstName: nameSchema('firstName'),
    lastName: nameSchema('lastName'),
    document: documentSchema
});

const DataField = ({ label, value, alignValues }) => (
    <Row mY={2}>
        <Column>
            { label }
        </Column>
        <Column align={alignValues}>
            { value }
        </Column>
    </Row>
);

DataField.propTypes = {
    label: oneOfType([array, object]),
    value: oneOfType([array, object]),
    alignValues: string
};

DataField.defaultProps = {
    label: {},
    value: {},
    alignValues: 'flex-start'
};

const ReceiverInfo = ({ data }) => {
    const [visible, setVisible] = useState(false);
    const [firstName, setFirstName] = useState(null);
    const [lastName, setLastName] = useState(null);
    const [document, setDocument] = useState(null);
    const [fieldErrors, setFieldErrors] = useState(null);
    const [mutationError, setMutationError] = useState(false);

    const errorsResolver = ({ errors }, reset = true) => {
        if (reset) setFieldErrors(null);
        let errorsMemory = reset ? {} : { ...fieldErrors };
        forEach(errors, error => {
            const splited = split(error, '-');
            errorsMemory = { ...errorsMemory, [splited[0]]: splited[1] };
        });
        setFieldErrors(errorsMemory);
    };

    const removeError = field => {
        let errors = { ...fieldErrors };
        delete errors[field];
        setFieldErrors(errors);
    };

    const setFieldValue = async (value, callback, field) => {
        const result = get(value, 'target.value', !isEmpty(get(value, 'value', null)) ? get(value, 'value', null) : null);

        if (includes(['firstName', 'lastName'], field)) {
            nameSchema(field).validate(result)
            .then(_ => removeError(field))
            .catch(err => errorsResolver(err, false));
        };

        if (field === 'document') {
            documentSchema.validate(result)
                .then(_ => removeError('document'))
                .catch(err => errorsResolver(err, false));
        };

        callback(includes(['document'], field) ? format(result) : result);
    };

    const formFields = [
        {
            label: 'Nombre',
            onChange: value => setFieldValue(value, setFirstName, 'firstName'),
            error: get(fieldErrors, 'firstName', null)
        },
        {
            label: 'Apellido',
            onChange: value => setFieldValue(value, setLastName, 'lastName'),
            error: get(fieldErrors, 'lastName', null)
        },
        {
            label: 'RUT',
            onChange: value => setFieldValue(get(value, 'target.value') === '-' ? null : value, setDocument, 'document'),
            error: get(fieldErrors, 'document', null),
            value: document,
            isDocumentId: true,
            maxLength: 12
        }
    ];

    const validateFields = async (callback = () => {}) => {
        await validationSchema.validate({ firstName, lastName, document }, { abortEarly: false })
            .then(data => callback(data))
            .catch(errorsResolver);
    };

    const validStatuses = ['1', '2', '3', '4', '6', '7', '20', '30', '32'];

    const onClose = () => {
        setVisible(false);
        setFirstName(null);
        setLastName(null);
        setDocument(null);
        setFieldErrors(null);
        setMutationError(false);
    };

    return (
        <>
            <DataField
                label={<Text strong text='order_detail.receiver_name' />}
                value={
                    <FlexContainer style={{ flexFlow: 'nowrap' }}>
                        <Text text={`${get(data, 'receiverData.firstName')} ${get(data, 'receiverData.lastName')}`} />
                        <HasPermissions to={['order-detail-page:modify-receiver']}>
                            { includes(validStatuses, get(data, 'assignInfo.status.code', '0')) && (
                                <Button
                                    link
                                    style={{ border: 'none', minHeight: '20px' }}
                                    backgroundColor='transparent'
                                    width={30}
                                    onClick={() => setVisible(true)}
                                >
                                    <AppIcon name='ic-edit' size='12' />
                                </Button>
                            ) }
                        </HasPermissions>
                    </FlexContainer>
                }
            />
            <DataField
                label={<Text strong text='order_detail.receiver_document' />}
                value={<Text text={format(get(data, 'receiverData.document', '-'))} />}
            />
            <Modal
                visible={visible}
                width={400}
                title='Modificar quien retira/recibe'
                onClose={onClose}
            >
                <FlexContainer justify='center'>
                    <FlexContainer direction='column' width={160} mB={25}>
                        { visible && map(formFields, (props, index) => (
                            <FormInput
                                key={index}
                                {...props}
                            />
                        )) }
                        <Mutation
                            mutation={UPDATE_ORDER}
                            refetchQueries={['getOrderById']}
                            onCompleted={onClose}
                            onError={() => setMutationError(true)}
                        >
                            {({ loading, updateFn }) => {
                                const onSubmit = async () => updateFn({
                                    variables: {
                                        input: {
                                            idOrder: get(data, 'id'),
                                            document: document || get(data, 'receiverData.document', null),
                                            firstName: firstName || get(data, 'receiverData.firstName'),
                                            lastName: lastName || get(data, 'receiverData.lastName'),
                                            user: get(await currentUserInfo(), 'email')
                                        }
                                    }
                                });
                                return (
                                    <Button
                                        loading={loading}
                                        text={mutationError ? 'Reintentar' : 'Guardar'}
                                        // loading={loading}
                                        // disabled={loading || !firstName || !lastName || !document || !email || !phone || !company || !salesChannels}
                                        onClick={() => validateFields(onSubmit)}
                                    />
                                );
                            }}
                        </Mutation>
                    </FlexContainer>
                </FlexContainer>
            </Modal>
        </>
    );
};

ReceiverInfo.propTypes = {
    data: oneOfType([array, object])
};

ReceiverInfo.defaultProps = {
    data: {}
};

export default ReceiverInfo;
